import { createContext, useEffect, useState, useCallback, use } from 'react';
import type { ReactNode } from 'react';
import { LazyDataLoader } from '@/lib/utils/lazy-data-loader';
import { DBCollection } from '@/lib/utils/constants-shared';

interface ArchaeologyByPassageDataContextType {
  archaeologyByPassage: ArchaeologyByPassage[];
  getArchaeologyByPassageByPassageId: (passageId: string) => ArchaeologyByPassage | undefined;
  loadArchaeologyByPassageByPassageId: (passageId: string) => Promise<ArchaeologyByPassage | undefined>;
}

export const ArchaeologyByPassageDataContext = createContext<ArchaeologyByPassageDataContextType | undefined>(undefined);

interface ArchaeologyByPassageDataProviderProps {
  children: ReactNode;
  archaeologyByPassage: ArchaeologyByPassage[];
}

export function ArchaeologyByPassageDataProvider({ children, archaeologyByPassage: initialData }: ArchaeologyByPassageDataProviderProps) {
  const [loader] = useState(() => new LazyDataLoader<ArchaeologyByPassage>(DBCollection.archaeologyByPassage, 'passageId'));
  const [cachedItems, setCachedItems] = useState<ArchaeologyByPassage[]>([]);

  // Preload any initial data into memory cache
  useEffect(() => {
    if (initialData && initialData.length > 0) {
      loader.preload(initialData);
      setCachedItems(loader.getAllCached());
    }
  }, [initialData, loader]);

  // Synchronous getter - returns from memory cache only
  const getArchaeologyByPassageByPassageId = useCallback((passageId: string): ArchaeologyByPassage | undefined => {
    return cachedItems.find(item => item.passageId === passageId);
  }, [cachedItems]);

  // Async loader - fetches if not in memory, updates state when loaded
  const loadArchaeologyByPassageByPassageId = useCallback(async (passageId: string): Promise<ArchaeologyByPassage | undefined> => {
    const item = await loader.get(passageId);
    if (item) {
      // Update cached items to trigger re-render
      setCachedItems(loader.getAllCached());
    }
    return item;
  }, [loader]);

  return (
    <ArchaeologyByPassageDataContext.Provider
      value={{
        archaeologyByPassage: cachedItems,
        getArchaeologyByPassageByPassageId,
        loadArchaeologyByPassageByPassageId
      }}
    >
      {children}
    </ArchaeologyByPassageDataContext.Provider>
  );
}

/**
 * Hook that auto-loads archaeology by passage data when accessed
 */
export function useArchaeologyByPassageData(passageId?: string) {
  const context = use(ArchaeologyByPassageDataContext);
  const [data, setData] = useState<ArchaeologyByPassage | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!passageId || !context) return;

    // Check if already in memory
    const cached = context.getArchaeologyByPassageByPassageId(passageId);
    if (cached) {
      setData(cached);
      return;
    }

    // Load from cache/firestore
    setIsLoading(true);
    context.loadArchaeologyByPassageByPassageId(passageId)
      .then(loaded => {
        setData(loaded);
        setIsLoading(false);
      })
      .catch(err => {
        console.error(`Failed to load archaeology by passage for ${passageId}:`, err);
        setIsLoading(false);
      });
  }, [passageId, context]);

  return { data, isLoading, context };
}

